import { useState } from 'react';
import styles from './PosterStrip.module.css';

/**
 * A fanned strip of real poster art or platform logos — the card image for a
 * featured workflow.
 *
 * Lifted from d360's PortfolioGallery: posters overlap left-to-right with a
 * hard right-edge shadow and descending z-index, so the first one sits on top
 * and the rest read as a stack. A darkening backdrop keeps any overlaid label
 * legible against arbitrary artwork.
 */

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';
const PLATFORM_CDN = 'https://cdn-fastly.parrotanalytics.com/platforms/tv360';

/** Same slug rule as PlatformIcon and d360: non-alphanumeric → underscore. */
function platformSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

function PosterImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className={styles.missing} />;
  return (
    <img
      src={src}
      alt={alt}
      className={styles.img}
      loading={'lazy'}
      onError={() => setFailed(true)}
    />
  );
}

export type StripSubject =
  | { kind: 'titles'; ids: string[] }
  | { kind: 'platforms'; names: string[] };

interface Props {
  subject: StripSubject;
  /** Short uppercase label pinned to the top-left. */
  tag?: string;
}

export function PosterStrip({ subject, tag }: Props) {
  const items =
    subject.kind === 'titles'
      ? subject.ids.slice(0, 6).map((id) => ({
          key: id,
          src: `${POSTER_CDN}/${id}.jpg?width=300`,
          alt: '',
        }))
      : subject.names.slice(0, 6).map((name) => ({
          key: name,
          src: `${PLATFORM_CDN}/${platformSlug(name)}.png?width=200`,
          alt: name,
        }));

  return (
    <div className={styles.base} data-kind={subject.kind}>
      <div className={styles.strip}>
        {items.map((item) => (
          <div key={item.key} className={styles.cell}>
            <PosterImage src={item.src} alt={item.alt} />
          </div>
        ))}
      </div>
      <div className={styles.backdrop} />
      {tag && <span className={styles.tag}>{tag}</span>}
    </div>
  );
}
